import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import {
  fetchNewsletterPreview,
  getNewsletterPreviewEmptyCode,
  isNewsletterPreviewEmptyError,
  newsletterPreviewEmptyMessage,
  type NewsletterPreviewResult,
  type PreviewNewsletterInput,
} from '../../lib/previewNewsletter';
import {
  POSTMARK_PREVIEW_IFRAME_CLASS,
  POSTMARK_PREVIEW_PANEL_CLASS,
} from '../PostmarkTemplates/postmarkTemplateLocalPreview';

interface NewsletterContentPreviewProps {
  previewInput: PreviewNewsletterInput;
  /** Bump to force refresh (e.g. after autosave). */
  refreshKey?: number;
}

export default function NewsletterContentPreview({
  previewInput,
  refreshKey = 0,
}: NewsletterContentPreviewProps) {
  const [result, setResult] = useState<NewsletterPreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const previewInputKey = useMemo(() => JSON.stringify(previewInput), [previewInput]);

  const load = useCallback(async () => {
    const content = previewInput.content;
    if (!Array.isArray(content) || content.length === 0) {
      setResult(null);
      setError(null);
      setEmptyMessage(null);
      setLoading(false);
      return;
    }

    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setEmptyMessage(null);
    try {
      const next = await fetchNewsletterPreview(previewInput);
      if (id !== requestIdRef.current) return;
      setResult(next);
    } catch (e) {
      if (id !== requestIdRef.current) return;
      setResult(null);
      if (isNewsletterPreviewEmptyError(e)) {
        setEmptyMessage(newsletterPreviewEmptyMessage(getNewsletterPreviewEmptyCode(e)));
        setError(null);
      } else {
        setEmptyMessage(null);
        setError(e instanceof Error ? e.message : 'Preview failed');
      }
    } finally {
      if (id === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [previewInput]);

  useEffect(() => {
    const content = previewInput.content;
    if (!Array.isArray(content) || content.length === 0) {
      setResult(null);
      setError(null);
      setEmptyMessage(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const t = window.setTimeout(() => {
      void load();
    }, 700);
    return () => window.clearTimeout(t);
  }, [previewInputKey, refreshKey, load, previewInput.content]);

  const blocksEmpty =
    !Array.isArray(previewInput.content) || previewInput.content.length === 0;

  if (blocksEmpty) {
    return (
      <p className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
        Nothing to preview yet. Add content in Edit or JSON mode.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {result ? (
            <>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {result.previewKind === 'template' ? 'Postmark template' : 'Raw HTML'}
              </span>
              {result.postmarkTemplateAlias ? (
                <span className="font-mono ml-1">{result.postmarkTemplateAlias}</span>
              ) : null}
              {result.subject ? (
                <span className="ml-2 text-gray-400">· {result.subject}</span>
              ) : null}
            </>
          ) : emptyMessage ? (
            'No rendered content yet'
          ) : loading ? (
            'Loading send preview…'
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh"
          className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {result?.warning ? (
        <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
          {result.warning}
        </p>
      ) : null}

      <div className={POSTMARK_PREVIEW_PANEL_CLASS}>
        {loading && !result && !emptyMessage ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
          </div>
        ) : emptyMessage ? (
          <p className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">{emptyMessage}</p>
        ) : error ? (
          <p className="p-8 text-sm text-red-700 dark:text-red-300 text-center">{error}</p>
        ) : result?.html ? (
          <iframe
            title="Newsletter send preview"
            srcDoc={result.html}
            className={POSTMARK_PREVIEW_IFRAME_CLASS}
            sandbox="allow-same-origin"
          />
        ) : null}
      </div>
    </div>
  );
}
